import { useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api, errMsg, fmtDate } from "../lib/api";
import { useApi, Panel, btn, inputCls } from "./kit";

export function LessonFeedback({ aid, sid, li, title }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/lesson-feedback", { assignment_id: aid, section_id: sid, lesson_index: li, note }); toast.success("Thanks! Your note was sent to the course editors."); setNote(""); setOpen(false); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button type="button" className="ml-auto inline-flex items-center gap-1.5 font-lesson text-sm text-[color:var(--lesson-mute)] underline-offset-4 hover:underline" data-testid="lesson-feedback-button">
        <Flag className="h-4 w-4" /> Spot a mistake?</button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="lesson-feedback-dialog">
        <DialogHeader><DialogTitle>Flag something in "{title}"</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <p className="text-sm text-muted-foreground">A typo, a wrong date or fact, a photo that doesn't match? Tell us what you noticed and it goes straight to the people who edit this course.</p>
          <textarea className={inputCls} rows={4} required maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. City Seltzer launched in 2020, not 2021" aria-label="What looks wrong?" data-testid="lesson-feedback-note" />
          <button className={`${btn.primary} w-full`} disabled={busy || !note.trim()} data-testid="lesson-feedback-send">{busy ? "Sending…" : "Send note"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackInbox() {
  const [status, setStatus] = useState("open");
  const { data, reload } = useApi(`/lesson-feedback?status=${status}`);
  const mark = async (f, s) => { try { await api.patch(`/lesson-feedback/${f.id}`, { status: s }); toast.success(s === "fixed" ? "Marked fixed." : "Reopened."); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data || (status === "open" && !data.length)) return status === "open" && data ? <button className="text-sm font-semibold text-muted-foreground underline-offset-4 hover:underline" onClick={() => setStatus("fixed")} data-testid="feedback-show-fixed">No open lesson feedback · see fixed notes</button> : null;
  return (
    <Panel title={<span className="flex items-center gap-2"><Flag className="h-5 w-5" /> Lesson feedback {status === "open" ? `(${data.length} open)` : "(fixed)"}</span>} testid="feedback-inbox"
      action={<button className="text-sm font-semibold text-[color:var(--dcbc-red)]" onClick={() => setStatus(status === "open" ? "fixed" : "open")} data-testid="feedback-toggle">{status === "open" ? "Show fixed" : "Show open"}</button>}>
      {!data.length && <p className="text-sm text-muted-foreground">Nothing here.</p>}
      <ul className="space-y-3">{data.map((f) => (
        <li key={f.id} className="rounded-xl border bg-white p-4" data-testid={`feedback-${f.id}`}>
          <p className="text-xs text-muted-foreground"><Link to={`/admin/courses/${f.course_id}`} className="font-semibold text-[color:var(--ink)] hover:underline">{f.course_title}</Link> › {f.section_title} › {f.lesson_title}</p>
          <p className="mt-1 whitespace-pre-line">{f.note}</p>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>From {f.user_name} · {fmtDate(f.created_at)}{f.resolved_by ? ` · fixed by ${f.resolved_by}` : ""}</span>
            {f.status === "open" ? <button className={`${btn.ghost} ml-auto`} onClick={() => mark(f, "fixed")} data-testid={`feedback-fix-${f.id}`}><Check className="h-4 w-4" /> Mark fixed</button>
              : <button className={`${btn.ghost} ml-auto`} onClick={() => mark(f, "open")} data-testid={`feedback-reopen-${f.id}`}><RotateCcw className="h-4 w-4" /> Reopen</button>}
          </div>
        </li>))}</ul>
    </Panel>
  );
}
